// Stripped-down home for the large/simple display mode: a friendly greeting,
// the next upcoming shift, and three big buttons. Reuses the QuickBook modal so
// booking stays a single, simple screen.
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { api } from '../api'
import { useI18n } from '../i18n'
import type { Booking, JobRole } from '../types'
import { addDays, formatHour, formatLong, parseYmd, todayYmd, ymd } from '../lib/dates'
import { Spinner } from './ui'
import QuickBook from './QuickBook'
import { IconCalendar, IconClock, IconPlus } from './icons'

const sortKey = (b: Booking) => b.date + String(b.hour).padStart(2, '0')

export default function SimpleHome() {
  const { user } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const [next, setNext] = useState<Booking | null>(null)
  const [roles, setRoles] = useState<JobRole[]>([])
  const [loading, setLoading] = useState(true)
  const [booking, setBooking] = useState(false)

  const load = useCallback(async () => {
    const today = parseYmd(todayYmd())
    try {
      const [mine, r] = await Promise.all([
        api.bookings(ymd(today), ymd(addDays(today, 180)), 'mine'),
        api.roles().catch(() => ({ roles: [] as JobRole[] })),
      ])
      const upcoming = mine.bookings
        .filter((b) => b.status === 'pending' || b.status === 'approved')
        .sort((a, b) => sortKey(a).localeCompare(sortKey(b)))
      setNext(upcoming[0] ?? null)
      setRoles(r.roles)
    } catch {
      // best-effort — the big buttons still work without this data
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const firstName = (user?.name || '').split(' ')[0]

  if (loading) {
    return (
      <div className="full-loader" style={{ minHeight: 320 }}>
        <Spinner large />
      </div>
    )
  }

  return (
    <div className="simple-home">
      <h1 className="simple-hello">{firstName ? t('simple.hello', { name: firstName }) : t('topbar.welcome')}</h1>

      <div className="simple-next card">
        <div className="simple-next-label">{t('simple.next')}</div>
        {next ? (
          <div className="simple-next-value">
            {formatLong(next.date)}
            <br />
            {formatHour(next.hour)}–{formatHour(next.hour + 1)}
          </div>
        ) : (
          <div className="simple-next-empty">{t('simple.noShifts')}</div>
        )}
      </div>

      <div className="simple-actions">
        <button type="button" className="simple-btn primary" onClick={() => setBooking(true)}>
          <IconPlus width={36} height={36} />
          <span>{t('simple.book')}</span>
        </button>
        <button type="button" className="simple-btn" onClick={() => navigate('/my-shifts')}>
          <IconClock width={36} height={36} />
          <span>{t('simple.myShifts')}</span>
        </button>
        <button type="button" className="simple-btn" onClick={() => navigate('/calendar')}>
          <IconCalendar width={36} height={36} />
          <span>{t('simple.calendar')}</span>
        </button>
      </div>

      {booking && <QuickBook roles={roles} onClose={() => setBooking(false)} onBooked={load} />}
    </div>
  )
}
